import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { UIBlock } from './types';

/* ── Cloudscape chart components (resolved once at module scope) ── */
let CloudscapeLineChart: any;
let CloudscapeBarChart: any;
let CloudscapePieChart: any;
let CloudscapeBox: any;
try {
  CloudscapeLineChart = require('@cloudscape-design/components/line-chart').default;
  CloudscapeBarChart = require('@cloudscape-design/components/bar-chart').default;
  CloudscapePieChart = require('@cloudscape-design/components/pie-chart').default;
  CloudscapeBox = require('@cloudscape-design/components/box').default;
} catch {
  // Cloudscape chart components not available
}

/* ── Individual Block Renderers ── */

function TextBlock({ block }: { block: Extract<UIBlock, { type: 'text' }> }) {
  if (block.markdown) {
    return <div className="rv-block-text"><ReactMarkdown plugins={[remarkGfm]}>{block.text}</ReactMarkdown></div>;
  }
  return <div className="rv-block-text"><p>{block.text}</p></div>;
}

function MetricBlock({ block }: { block: Extract<UIBlock, { type: 'metric' }> }) {
  const trendIcon = block.trend === 'up' ? '▲' : block.trend === 'down' ? '▼' : '';
  const trendClass = block.trend === 'up' ? 'rv-trend-up' : block.trend === 'down' ? 'rv-trend-down' : 'rv-trend-flat';
  return (
    <div className="rv-block-metric">
      <span className="rv-metric-label">{block.label}</span>
      <span className="rv-metric-value">{block.value}</span>
      {block.trend && <span className={`rv-metric-trend ${trendClass}`}>{trendIcon}</span>}
    </div>
  );
}

function TableBlock({ block }: { block: Extract<UIBlock, { type: 'table' }> }) {
  // Columns can be strings or objects with {name, key}
  const getColLabel = (col: any): string => {
    if (typeof col === 'string') return col;
    if (col && typeof col === 'object') {
      const label = col.name || col.key;
      return typeof label === 'string' ? label : JSON.stringify(col);
    }
    return String(col);
  };
  const getColKey = (col: any): string => {
    if (typeof col === 'string') return col;
    if (col && typeof col === 'object') {
      const key = col.key || col.name;
      return typeof key === 'string' ? key : JSON.stringify(col);
    }
    return String(col);
  };

  return (
    <div className="rv-block-table">
      <table>
        <thead>
          <tr>{block.columns.map((col: any, i: number) => {
            const label = getColLabel(col);
            return <th key={i}>{typeof label === 'object' ? JSON.stringify(label) : label}</th>;
          })}</tr>
        </thead>
        <tbody>
          {block.rows.map((row: any, i: number) => (
            <tr key={i}>
              {Array.isArray(row)
                ? row.map((cell: any, j: number) => <td key={j}>{typeof cell === 'object' && cell !== null ? JSON.stringify(cell) : cell}</td>)
                : block.columns.map((col: any, j: number) => {
                    const val = row[getColKey(col)] ?? '';
                    return <td key={j}>{typeof val === 'object' && val !== null ? JSON.stringify(val) : val}</td>;
                  })
              }
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartBlock({ block }: { block: Extract<UIBlock, { type: 'chart' }> }) {
  if (!CloudscapeLineChart || !CloudscapeBarChart || !CloudscapePieChart || !CloudscapeBox) {
    return <div className="rv-block-chart"><pre>{JSON.stringify(block.data, null, 2)}</pre></div>;
  }

  if (block.chartType === 'pie') {
    const segments = block.data.map(d => ({
      title: String(d[block.xKey]),
      value: d[block.yKeys[0]],
    }));
    return (
      <div className="rv-block-chart">
        <CloudscapePieChart data={segments} size="medium" hideFilter empty={<CloudscapeBox>No data</CloudscapeBox>} />
      </div>
    );
  }

  const series = block.yKeys.map(key => ({
    title: key,
    type: block.chartType === 'bar' ? 'bar' as const : 'line' as const,
    data: block.data.map(d => ({ x: String(d[block.xKey]), y: Number(d[key]) })),
  }));

  const ChartComponent = block.chartType === 'bar' ? CloudscapeBarChart : CloudscapeLineChart;

  return (
    <div className="rv-block-chart">
      <ChartComponent
        series={series}
        height={220}
        xScaleType="categorical"
        xTitle={block.xKey}
        yTitle={block.yKeys[0]}
        hideFilter
        hideLegend={series.length <= 1}
        empty={<CloudscapeBox textAlign="center">No data</CloudscapeBox>}
      />
    </div>
  );
}

function ImageBlock({ block }: { block: Extract<UIBlock, { type: 'image' }> }) {
  return <div className="rv-block-image"><img src={block.url} alt={block.alt || ''} /></div>;
}

function CodeBlock({ block }: { block: Extract<UIBlock, { type: 'code' }> }) {
  return (
    <div className="rv-block-code">
      <pre><code className={`language-${block.language}`}>{block.content}</code></pre>
    </div>
  );
}

function CalloutBlock({ block }: { block: Extract<UIBlock, { type: 'callout' }> }) {
  const icons = { info: 'ℹ️', warning: '⚠️', error: '❌', success: '✅' };
  // Strip leading emoji from text to avoid duplication with the icon
  // Handles multi-codepoint emoji like ⚠️ (U+26A0 + U+FE0F), ✅, ❌, ℹ️, etc.
  const cleanText = block.text.replace(/^[\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F300}-\u{1F9FF}\u{200D}\u{20E3}\u{E0020}-\u{E007F}\u{2702}-\u{27B0}\u{FE0F}]+\s*/u, '').trim();
  return (
    <div className={`rv-block-callout rv-callout-${block.level}`}>
      <span className="rv-callout-icon">{icons[block.level]}</span>
      <div>
        {block.title && <strong className="rv-callout-title">{block.title}</strong>}
        <div className="rv-callout-text"><ReactMarkdown plugins={[remarkGfm]}>{cleanText}</ReactMarkdown></div>
      </div>
    </div>
  );
}

function ActionsBlock({ block }: { block: Extract<UIBlock, { type: 'actions' }> }) {
  return (
    <div className="rv-block-actions">
      {block.actions.map((action, i) => (
        <button key={i} className="rv-action-btn" data-action-id={action.actionId}>
          {action.label}
        </button>
      ))}
    </div>
  );
}

function TimelineBlock({ block }: { block: Extract<UIBlock, { type: 'timeline' }> }) {
  return (
    <div className="rv-block-timeline">
      {block.items.map((item, i) => (
        <div key={i} className="rv-timeline-item">
          <span className="rv-timeline-time">{item.time}</span>
          <span className="rv-timeline-label">{item.label}</span>
          {item.description && <span className="rv-timeline-desc">{item.description}</span>}
        </div>
      ))}
    </div>
  );
}

function JsonBlock({ block }: { block: Extract<UIBlock, { type: 'json' }> }) {
  return (
    <div className="rv-block-json">
      <pre>{JSON.stringify(block.data, null, 2)}</pre>
    </div>
  );
}

/* ── Main ResultRenderer ── */

export function ResultRenderer({ blocks }: { blocks: UIBlock[] }) {
  return (
    <div className="rv-result-renderer">
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'text': return <TextBlock key={i} block={block} />;
          case 'metric': return <MetricBlock key={i} block={block} />;
          case 'table': return <TableBlock key={i} block={block} />;
          case 'chart': return <ChartBlock key={i} block={block} />;
          case 'image': return <ImageBlock key={i} block={block} />;
          case 'code': return <CodeBlock key={i} block={block} />;
          case 'callout': return <CalloutBlock key={i} block={block} />;
          case 'actions': return <ActionsBlock key={i} block={block} />;
          case 'timeline': return <TimelineBlock key={i} block={block} />;
          case 'json': return <JsonBlock key={i} block={block} />;
          default: return null;
        }
      })}
    </div>
  );
}
